import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { MathSymbol } from '../utils/mathSymbols';

interface MathAutocompleteProps {
    visible: boolean;
    position: { x: number; y: number };
    suggestions: MathSymbol[];
    selectedIndex: number;
    onSelect: (symbol: MathSymbol) => void;
    onClose: () => void;
}

export const MathAutocomplete: React.FC<MathAutocompleteProps> = ({
    visible,
    position,
    suggestions,
    selectedIndex,
    onSelect,
    onClose
}) => {
    const listRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const list = listRef.current;
        if (!list) return;
        const item = list.children[selectedIndex] as HTMLElement | undefined;
        item?.scrollIntoView({ block: 'nearest' });
    }, [selectedIndex]);

    if (!visible || suggestions.length === 0) return null;

    return createPortal(
        <div
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
            className="fixed z-[9999] flex flex-col bg-white dark:bg-slate-800 rounded-lg shadow-2xl border border-gray-200 dark:border-slate-700 overflow-hidden animate-in fade-in zoom-in-95 duration-150"
            style={{
                left: position.x,
                top: position.y,
                minWidth: '200px',
                maxWidth: '300px',
                maxHeight: 'min(320px, 60vh)'
            }}
        >
            <div className="flex items-center justify-between px-2 py-1 border-b border-gray-100 dark:border-slate-700 shrink-0">
                <span className="text-[10px] font-bold uppercase tracking-wider text-gray-400 dark:text-gray-500 pl-1">
                    {suggestions.length} {suggestions.length === 1 ? 'symbol' : 'symbols'}
                </span>
                <button
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={onClose}
                    className="p-1 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:text-gray-200 dark:hover:bg-slate-700 active:scale-95 transition-colors"
                    title="Close"
                >
                    <X size={14} />
                </button>
            </div>
            <div ref={listRef} className="py-1 overflow-y-auto overscroll-contain">
                {suggestions.map((symbol, index) => (
                    <button
                        key={symbol.unicode}
                        onMouseDown={(e) => e.preventDefault()} // Prevent textarea blur
                        onClick={() => onSelect(symbol)}
                        className={`w-full px-3 py-2 flex items-center gap-3 transition-colors text-left ${index === selectedIndex
                            ? 'bg-indigo-100 dark:bg-indigo-900/30'
                            : 'hover:bg-gray-100 dark:hover:bg-slate-700'
                            }`}
                    >
                        <span className="text-2xl font-serif">{symbol.unicode}</span>
                        <div className="flex-1 min-w-0">
                            <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                                {symbol.description}
                            </div>
                            <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                                {symbol.trigger.join(', ')}
                            </div>
                        </div>
                    </button>
                ))}
            </div>
            <div className="px-3 py-1.5 bg-gray-50 dark:bg-slate-900 border-t border-gray-200 dark:border-slate-700 shrink-0">
                <div className="text-xs text-gray-500 dark:text-gray-400">
                    <kbd className="px-1.5 py-0.5 bg-white dark:bg-slate-800 border border-gray-300 dark:border-slate-600 rounded text-xs">↑↓</kbd> Navigate
                    {' '}
                    <kbd className="px-1.5 py-0.5 bg-white dark:bg-slate-800 border border-gray-300 dark:border-slate-600 rounded text-xs">Enter</kbd> Select
                    {' '}
                    <kbd className="px-1.5 py-0.5 bg-white dark:bg-slate-800 border border-gray-300 dark:border-slate-600 rounded text-xs">Esc</kbd> Close
                </div>
            </div>
        </div>,
        document.body
    );
};
